import React, { useMemo, useState, useContext, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { numberToWords, AmountInWords } from "../../../Utility/function";
import { WarehouseContext } from '../../../Utility/UserContext';

const SalesReport_PDF = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { shopItem } = useContext(WarehouseContext);

    const [selectedLanguage] = useState("english");
    const [expandedIndex, setExpandedIndex] = useState(null);
    // Destructure with default values to prevent errors
    const {
        InvoiceDetail = {},
        Products = [],
        ReturnItem = [],
        Payment = [],
    } = location.state || {};


    const paidAmount = useMemo(() =>
        Payment.filter(p => p.mop !== "2")
            .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
            .toFixed(3),
        [Payment]
    );


    // Memoized calculations
    const productsTotal = useMemo(() =>
        Products.reduce((acc, item) => acc + (parseFloat(item?.p_price) || 0), 0).toFixed(3),
        [Products]
    );

    const returnTotal = useMemo(() =>
        ReturnItem.reduce((acc, item) => acc + (parseFloat(item.p_price) || 0), 0).toFixed(3),
        [ReturnItem]
    );

    const grandTotal = parseFloat(productsTotal || 0).toFixed(3);


    const taxAmount = useMemo(() =>
        ((parseFloat(productsTotal) / 100) * (InvoiceDetail.vat || 0)).toFixed(3),
        [productsTotal, InvoiceDetail.vat]
    );

    const remainingBalance = useMemo(() =>
        (parseFloat(grandTotal || 0) - parseFloat(paidAmount || 0)).toFixed(3),
        [grandTotal, paidAmount]
    );

    

    // Memoized customer details section
    const customerDetails = useMemo(() => (
        <div className="p-2" style={{ border: ".8px solid #dee2e6", minHeight: 150 }}>
            <h6 className="fw-bold">
                {selectedLanguage === "arabic" ? "تفاصيل العميل:" : "Customer Details:"}
            </h6>
            {[
                { key: "name", label: selectedLanguage === "arabic" ? "الاسم:" : "Name:", value: InvoiceDetail.customer_name },
                { key: "email", label: selectedLanguage === "arabic" ? "البريد الإلكتروني:" : "Email:", value: InvoiceDetail.customer_email },
                { key: "address", label: selectedLanguage === "arabic" ? "العنوان:" : "Address:", value: InvoiceDetail.additional_details },
                { key: "phone", label: selectedLanguage === "arabic" ? "الهاتف:" : "Phone:", value: InvoiceDetail.customer_phone }
            ].map((item) => (
                <div key={item.key} className="d-flex justify-content-between">
                    <p className="fw-medium font-13 mb-1">{item.label}</p>
                    <p className="fw-bold font-13 mb-1" style={{ textDecoration: "underline" }}>
                        {item.value}
                    </p>
                </div>
            ))}
        </div>
    ), [selectedLanguage, InvoiceDetail]);

    // Memoized invoice info section
    const invoiceInfo = useMemo(() => (
        <>
            <table className="table table-bordered text-center align-middle m-0">
                <tbody>
                    <tr className='hight-header'>
                        <td className="text-start fw-bold">
                            {selectedLanguage === "arabic" ? "رقم الفاتورة:" : "Invoice No:"}
                            {InvoiceDetail?.order_id}
                        </td>
                        <td className="text-start fw-bold">
                            {selectedLanguage === "arabic" ? "التاريخ:" : "Date:"}{" "}
                            {InvoiceDetail?.date_time.split('T')[0]}
                        </td>
                    </tr>
                </tbody>
            </table>
            <table className="table table-bordered text-center align-middle">
                <tbody>
                    {[
                        { key: "lpo", label: selectedLanguage === "arabic" ? "رقم الطلب:" : "L.P.O No:", value: InvoiceDetail.lpo_no },
                        { key: "sales", label: selectedLanguage === "arabic" ? "البائع:" : "Sales Person:", value: InvoiceDetail.user_name }
                    ].map((item) => (
                        <tr key={item.key}>
                            <td className="text-start d-flex flex-row">
                                <p className="fw-bold text-start mb-0" style={{ width: "40%" }}>
                                    {item.label}
                                </p>
                                <p className="text-start mb-0" style={{ width: "60%" }}>
                                    {item.value}
                                </p>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    ), [selectedLanguage, InvoiceDetail]);

    // Memoized table headers
    const tableHeaders = useMemo(() => [
        { key: "sl", label: selectedLanguage === "arabic" ? "الرقم" : "SL No." },
        { key: "particulars", label: selectedLanguage === "arabic" ? "التفاصيل" : "Particulars" },
        { key: "qty", label: selectedLanguage === "arabic" ? "الكمية" : "Qty" },
        // { key: "unit", label: selectedLanguage === "arabic" ? "سعر الوحدة" : "Unit Price" },
        { key: "discount", label: selectedLanguage === "arabic" ? "الخصم" : "Offer" },
        { key: "total", label: selectedLanguage === "arabic" ? "المبلغ الإجمالي" : "Total Amount" }
    ], [selectedLanguage]);

    // Toggle function with useCallback
    const toggleExpand = useCallback((index) => {
        setExpandedIndex(prev => (prev === index ? null : index));
    }, []);

    // Memoized product rows
    const productRows = useMemo(() =>
        Products.map((item, index) => {
            const serial = JSON.parse(item.serial_number || "[]");
            const serials = serial.map(i => i.serial_number);

            return (
                <tr key={index} className="bg-transparent">
                    <td>{index + 1}</td>
                    <td style={{ maxWidth: "240px" }}>
                        <div>{item.product_title || item.product_name} {item.pbox_code === "FREE SIZE / LOSE QTY" || item.p_code === "FREE_SIZE" ? "(Lose Packs)" : `( ${item.p_weight} ${item.pbox_unit} )`}</div>
                        {/* {serials.length > 0 ? (
                            <div style={{ fontSize: "0.85rem", color: "#6c757d" }}>
                                {serials.join(", ")}
                            </div>
                        ) : (
                            <div style={{ fontSize: "0.80rem", color: "#ff0000", opacity: 0.55 }}>
                                No serial numbers available
                            </div>
                        )} */}
                    </td>
                    <td>
                        {item?.is_box == 1
                            ? `${item.boxqty} (x${parseInt(item.qty) || 0 / parseInt(item.boxqty) || 0})`
                            : item.qty
                        }
                    </td>
                    {/* <td>{Number(item.sales_price).toFixed(3)}</td> */}
                    <td>
                    {item.is_free =="1" ? "Offer Item" : "Normal Sale"}   {/* {item.discount || 0} 
                     {item.discount_type == "1" ? "%" : "RO"} */}
                    </td>
                    <td onClick={() => toggleExpand(index)} style={{ cursor: "pointer" }}>
                        {Number(item.p_price).toFixed(3)}
                    </td>
                </tr>
            );
        }),
        [Products, toggleExpand]
    );

    // Memoized return item rows
    const returnItemRows = useMemo(() =>
        ReturnItem.length > 0 && (
            <>
                <tr>
                    <td colSpan="6" className="text-center">
                        <strong>
                            {selectedLanguage === "arabic" ? "إرجاع المنتجات" : "Return Products"}
                        </strong>
                    </td>
                </tr>
                {ReturnItem.map((item, index) => (
                    <tr key={`return-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.product_title}</td>
                        <td>{item.qty}</td>
                        {/* <td>{item.sales_price}</td> */}
                        <td>
                            {item.discount || 0} {item.discount_type == "1" ? "%" : "RO"}
                        </td>
                        <td className="text-danger">{"- " + (item.p_price || "N/A")}</td>
                    </tr>
                ))}
            </>
        ),
        [ReturnItem, selectedLanguage]
    );

    // Memoized payment history
    const paymentHistory = useMemo(() =>
        InvoiceDetail?.purchase_unit != 1 && (
            <>
                <tr>
                    <td colSpan={5} className="text-center">
                        Payment History
                    </td>
                </tr>
                {Payment
                    ?.filter(item => item.mop != "2")
                    ?.map((item, index) => (
                        <tr key={`payment-${index}`}>
                            <td colSpan={3} className="text-end fw-bold px-2">
                                {item.date}
                            </td>
                            <td colSpan={1} className="text-end fw-bold px-2">
                                {item.mop == "1" ? "Cash" : item.mop == "4" ? "Card" : ""}
                            </td>
                            <td colSpan={1} className="text-center">
                                {Number(item.amount || 0).toFixed(3)}
                            </td>
                        </tr>
                    ))
                }
                <tr>
                    <td colSpan={4} className="text-end fw-bold px-2">
                        Remaining Balance
                    </td>
                    <td colSpan={1} className="text-center text-danger">
                        {remainingBalance}
                    </td>
                </tr>
            </>
        ),
        [InvoiceDetail, Payment, remainingBalance]
    );

    return (
        <div className="container my-3 bg-white border col-12">
            <div className="row border-top border-bottom pt-3 pb-2">
                <div className="col-6">
                    {customerDetails}
                </div>
                <div className="col-6">
                    {invoiceInfo}
                </div>
            </div>

            <div className="row p-3" style={{
                position: "relative",
                overflow: "hidden",
                backgroundSize: "60%",
                backgroundPosition: "center",
            }}>
                <table className="table table-bordered text-center align-middle mt-1 mb-0 bg-transparent border-dark">
                    <thead className="bg-dark">
                        <tr className="bg-primary">
                            {tableHeaders.map(header => (
                                <th key={header.key} className="bg-transparent">
                                    <span>{header.label}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {productRows}
                        {returnItemRows}
                    </tbody>

                    <tfoot>
                        <tr><br /></tr>

                        {/* Summary Rows */}
                        {[
                            {
                                label: selectedLanguage === "arabic" ? "الإجمالي" : "Total",
                                value: productsTotal,
                                colSpan: 3
                            },
                            // {
                            //     label: selectedLanguage === "arabic" ? "الضريبة" : "Tax",
                            //     value: taxAmount,
                            //     colSpan: 3
                            // },
                            // {
                            //     label: selectedLanguage === "arabic" ? "إرجاع" : "Return",
                            //     value: `-${returnTotal}`,
                            //     className: "text-danger",
                            //     colSpan: 3
                            // },
                            // {
                            //     label: selectedLanguage === "arabic" ? "الإجمالي" : "Grand Total",
                            //     value: grandTotal,
                            //     colSpan: 3
                            // }
                        ].map((row, index) => (
                            <tr key={index}>
                                <td colSpan={row.colSpan}></td>
                                <td className="fw-bold text-end">{row.label}</td>
                                <td className={row.className || ""}>{row.value}</td>
                            </tr>
                        ))}

                        {paymentHistory}

                        <tr>
                            {/* <td colSpan={5} className="text-start">
                                <p className="my-2">
                                    <strong>
                                        {selectedLanguage === "arabic" ? "المبلغ بالأحرف:" : "Amount In Words:"}
                                        &nbsp;
                                        {AmountInWords(grandTotal)}
                                    </strong>
                                </p>
                            </td> */}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default React.memo(SalesReport_PDF);